import { useMemo, useState, useEffect } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { QuoteSearch } from "./QuoteSearch";
import { PlanningQuotesTable } from "./PlanningQuotesTable";
import { ChatTranscriptModal } from "./ChatTranscriptModal";

const PROJECT_TAGS = {
  extension:  { label: "Extension", cls: "bg-[#DCE9F5] text-[#12263A]" },
  loft:       { label: "Loft",      cls: "bg-[#EADBF3] text-[#5B2A83]" },
  garage:     { label: "Garage",    cls: "bg-[#FBE3D6] text-[#8A3D0A]" },
  new_build:  { label: "New build", cls: "bg-[#D8EED8] text-[#1F6B3B]" },
  other:      { label: "Other",     cls: "bg-[#F0EAE0] text-[#6B7280]" },
};
const BLOCK_REASON_LABELS = {
  "postcode-outside": "Outside instant-quote area",
  "rear-access-blocked": "No rear access for materials/machinery",
};

const PROJECT_ORDER = ["extension", "loft", "garage", "new_build", "other"];

function ProjectTag({ value }) {
  const key = (value || "").toString().trim().toLowerCase();
  const def = PROJECT_TAGS[key] || { label: "Unspecified", cls: "bg-white text-[#6B7280] border border-dashed border-[#E5E7EB]" };
  return (
    <span data-testid={`enquiry-project-tag-${key || "none"}`} className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${def.cls}`}>
      {def.label}
    </span>
  );
}

export function EnquiriesTab({ enquiries, planningQuotes, loadEnquiries, loadPlanningQuotes, loadCounts, setOpenRef, loadBookings }) {
  const [replyTo, setReplyTo] = useState(null);
  const [reply, setReply] = useState({ subject: "", body: "" });
  const [replyBusy, setReplyBusy] = useState(false);
  const [convertBusy, setConvertBusy] = useState(null);
  const [projectFilter, setProjectFilter] = useState("all");
  const [transcriptFor, setTranscriptFor] = useState(null);
  const [postcodeSearches, setPostcodeSearches] = useState({ items: [], groups: [], total: 0 });
  const [postcodeExpanded, setPostcodeExpanded] = useState(false);

  const loadPostcodeSearches = async () => {
    try {
      const r = await api.get("/admin/postcode-searches");
      setPostcodeSearches({
        items: r.data.items || [],
        groups: r.data.groups || [],
        total: r.data.total || 0,
      });
    } catch (e) { /* silent — panel just stays empty */ }
  };
  useEffect(() => { loadPostcodeSearches(); }, []);

  const markEnquiry = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      loadEnquiries(); loadCounts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update enquiry"); }
  };
  const convertEnquiry = async (e) => {
    setConvertBusy(e.id);
    try {
      const r = await api.post(`/admin/enquiries/${e.id}/convert`);
      toast.success(r.data.already ? `Opening existing job file ${r.data.reference}` : `Created draft job file ${r.data.reference}`);
      loadEnquiries(); loadCounts();
      if (loadBookings) loadBookings();
      if (setOpenRef) setOpenRef(r.data.reference);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not convert enquiry"); }
    setConvertBusy(null);
  };
  const openReply = (e) => {
    const first = (e.name || "there").split(" ")[0];
    setReply({
      subject: "Re: your enquiry to The Extension Works",
      body: `Hi ${first},\n\nThank you for getting in touch with The Extension Works${e.message ? ` about "${e.message}"` : ""}.\n\n\n\nKind regards,\nThe Extension Works team`,
    });
    setReplyTo(e);
  };
  const sendReply = async () => {
    if (!reply.subject.trim() || !reply.body.trim()) { toast.error("Subject and message are required"); return; }
    setReplyBusy(true);
    try {
      const r = await api.post(`/admin/enquiries/${replyTo.id}/reply`, { subject: reply.subject, body: reply.body });
      toast.success(`Reply sent to ${r.data.to}`);
      setReplyTo(null); loadEnquiries(); loadCounts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reply"); }
    setReplyBusy(false);
  };

  const counts = useMemo(() => {
    const c = { all: enquiries.length, none: 0 };
    PROJECT_ORDER.forEach(k => c[k] = 0);
    enquiries.forEach(e => {
      const k = (e.project_type || "").toString().trim().toLowerCase();
      if (PROJECT_ORDER.includes(k)) c[k]++;
      else c.none++;
    });
    return c;
  }, [enquiries]);

  const filteredEnquiries = useMemo(() => {
    if (projectFilter === "all") return enquiries;
    if (projectFilter === "none") return enquiries.filter(e => !PROJECT_ORDER.includes((e.project_type || "").toString().trim().toLowerCase()));
    return enquiries.filter(e => (e.project_type || "").toString().trim().toLowerCase() === projectFilter);
  }, [enquiries, projectFilter]);

  return (
    <div className="space-y-8">
      <QuoteSearch />

      <MissedPostcodesPanel
        data={postcodeSearches}
        expanded={postcodeExpanded}
        setExpanded={setPostcodeExpanded}
      />

      <div>
        <h2 className="text-lg font-bold tracking-tight mb-3">Blocked / contact enquiries</h2>

        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="enquiries-project-filter">
          {[
            ["all", "All"],
            ...PROJECT_ORDER.map(k => [k, PROJECT_TAGS[k].label]),
            ["none", "Unspecified"],
          ].map(([v, l]) => {
            const n = counts[v] || 0;
            const active = projectFilter === v;
            return (
              <button key={v} data-testid={`enquiries-filter-${v}`} onClick={() => setProjectFilter(v)}
                className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border inline-flex items-center gap-1.5 transition-colors ${active ? "bg-[#12263A] text-white border-[#12263A]" : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#12263A]"}`}>
                {l}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full leading-none ${active ? "bg-white/20 text-white" : "bg-[#F0EAE0] text-[#6B7280]"}`}>{n}</span>
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto border border-[#E5E7EB] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F4] text-left">
              <tr>{["Type", "Project", "Name", "Email", "Phone", "Postcode", "Message", "Files", "Created", "Action"].map(h => <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filteredEnquiries.map(e => {
                const isCallback = e.type === "callback_request";
                return (
                <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className={`border-t border-[#E5E7EB] align-top ${isCallback && e.status === "new" ? "bg-[#FDF7F5]/60" : ""}`}>
                  <td className="px-3 py-2">
                    {isCallback ? <span data-testid={`enquiry-type-callback-${e.id}`} className="inline-block bg-[#C0392B] text-white px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">Callback</span>
                      : e.type === "chatbot" ? <span className="inline-block bg-[#FBE3D6] text-[#C79A3B] px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">Daisy</span>
                      : e.type === "quote-blocked" ? <span data-testid={`enquiry-type-blocked-${e.id}`} title="This visitor tried to get an online quote but was screened out (out of area, or a property/site we can't quote online). Their details and the blocking reasons were captured so you can follow up." className="inline-block bg-[#F5E0E0] text-[#C0392B] px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider cursor-help">Quote blocked</span>
                      : e.type === "quote-abandoned" ? <span data-testid={`enquiry-type-abandoned-${e.id}`} title="Visitor left their contact details at the postcode step but never revealed a price. Use Create quote to pick up where they left off." className="inline-block bg-[#FFF4DE] text-[#A87E28] px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider cursor-help">Abandoned quote</span>
                      : e.type}
                  </td>
                  <td className="px-3 py-2"><ProjectTag value={e.project_type} /></td>
                  <td className="px-3 py-2">{e.name}</td>
                  <td className="px-3 py-2">{e.email}</td>
                  <td className="px-3 py-2">{e.phone}</td>
                  <td className="px-3 py-2">{e.postcode}</td>
                  <td className="px-3 py-2 text-xs max-w-sm">
                    {(e.reasons || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1" data-testid={`enquiry-reasons-${e.id}`}>
                        {e.reasons.map(r => (
                          <span key={r} className="inline-block bg-[#FDF0F0] text-[#C0392B] border border-[#F2C9C9] px-1.5 py-0.5 rounded text-[10px] font-semibold">
                            {BLOCK_REASON_LABELS[r] || r}
                          </span>
                        ))}
                      </div>
                    )}
                    {e.message}
                  </td>
                  <td className="px-3 py-2 text-xs">{(e.files || []).length || "—"}</td>
                  <td className="px-3 py-2 text-xs text-[#6B7280]">{new Date(e.created_at).toLocaleString("en-GB")}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {e.type === "chatbot" && (
                      <button data-testid={`enquiry-transcript-${e.id}`} onClick={() => setTranscriptFor(e)}
                        className="de-btn-secondary text-[11px] py-1 px-2 mr-1">Transcript</button>
                    )}
                    {e.converted_quote_ref && (
                      <span data-testid={`enquiry-quoted-${e.id}`}
                        className="inline-block mr-1 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest bg-[#FBF3EC] text-[#A87E28] border border-[#F0D5B4]">
                        Quoted · {e.converted_quote_ref}
                      </span>
                    )}
                    {!e.converted_booking_ref && !e.converted_quote_ref && (
                      <a data-testid={`enquiry-create-quote-${e.id}`} target="_blank" rel="noreferrer"
                        href={`/quote?enquiry=${e.id}&postcode=${encodeURIComponent(e.postcode || "")}&name=${encodeURIComponent(e.name || "")}&email=${encodeURIComponent(e.email || "")}&phone=${encodeURIComponent(e.phone || "")}`}
                        className="de-btn-secondary text-[11px] py-1 px-2 mr-1 inline-block">Create quote</a>
                    )}
                    {e.converted_booking_ref
                      ? <button data-testid={`enquiry-open-jobfile-${e.id}`} onClick={() => setOpenRef && setOpenRef(e.converted_booking_ref)} className="de-btn-secondary text-[11px] py-1 px-2 mr-1">Open job file</button>
                      : <button data-testid={`enquiry-convert-${e.id}`} onClick={() => convertEnquiry(e)} disabled={convertBusy === e.id} className="de-btn-primary text-[11px] py-1 px-2 mr-1 disabled:opacity-60">{convertBusy === e.id ? "Creating…" : "→ Job file"}</button>}
                    {e.email && <button data-testid={`enquiry-reply-${e.id}`} onClick={() => openReply(e)} className="de-btn-secondary text-[11px] py-1 px-2 mr-1">Reply</button>}
                    {e.status === "actioned"
                      ? <button data-testid={`enquiry-reopen-${e.id}`} onClick={() => markEnquiry(e.id, "new")} className="text-[#6B7280] hover:text-[#C79A3B]">✓ Actioned</button>
                      : <button data-testid={`enquiry-action-${e.id}`} onClick={() => markEnquiry(e.id, "actioned")} className="de-btn-secondary text-[11px] py-1 px-2">Mark actioned</button>}
                  </td>
                </tr>
                );
              })}
              {filteredEnquiries.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-[#6B7280]" data-testid="enquiries-empty">{projectFilter === "all" ? "No enquiries yet." : "No enquiries match this filter."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h2 className="text-lg font-bold tracking-tight mb-3">Planning-route quotes</h2>
        <PlanningQuotesTable quotes={planningQuotes} onReload={loadPlanningQuotes} />
      </div>

      {transcriptFor && <ChatTranscriptModal enquiry={transcriptFor} onClose={() => setTranscriptFor(null)} />}

      {replyTo && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !replyBusy && setReplyTo(null)}>
          <div className="bg-white border border-[#E5E7EB] w-full max-w-lg p-6" onClick={(ev) => ev.stopPropagation()} data-testid="enquiry-reply-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold tracking-tight">Reply to {replyTo.name || "enquiry"}</h3>
              <button onClick={() => !replyBusy && setReplyTo(null)} className="text-[#6B7280] hover:text-[#C79A3B]">✕</button>
            </div>
            <p className="text-xs text-[#6B7280] mb-4">Sending to <b>{replyTo.email}</b> via your connected Gmail. This marks the enquiry as actioned.</p>
            <label className="label-eyebrow block mb-1">Subject</label>
            <input data-testid="enquiry-reply-subject" value={reply.subject} onChange={(ev) => setReply({ ...reply, subject: ev.target.value })} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-3" />
            <label className="label-eyebrow block mb-1">Message</label>
            <textarea data-testid="enquiry-reply-body" value={reply.body} onChange={(ev) => setReply({ ...reply, body: ev.target.value })} rows={9} className="w-full border border-[#E5E7EB] px-3 py-2 text-sm mb-4 resize-y" />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setReplyTo(null)} disabled={replyBusy} className="de-btn-secondary text-sm">Cancel</button>
              <button data-testid="enquiry-reply-send" onClick={sendReply} disabled={replyBusy} className="de-btn-primary text-sm">{replyBusy ? "Sending…" : "Send reply"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function MissedPostcodesPanel({ data, expanded, setExpanded }) {
  const groups = data.groups || [];
  const items = data.items || [];
  const total = data.total || 0;
  if (total === 0) return null;
  const topGroups = groups.slice(0, expanded ? groups.length : 6);
  const recent = items.slice(0, expanded ? 40 : 8);
  return (
    <div className="border border-[#F0D5B4] bg-[#FBF3EC]" data-testid="missed-postcodes-panel">
      <div className="flex items-start gap-3 p-4">
        <div className="w-9 h-9 rounded-full bg-[#C79A3B] text-white flex items-center justify-center shrink-0 mt-0.5">
          <MapPin size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-bold text-sm tracking-tight text-[#12263A]" data-testid="missed-postcodes-title">Missed instant-quote postcodes</div>
              <div className="text-xs text-[#6B7280] mt-0.5">
                <span data-testid="missed-postcodes-total">{total}</span> failed search{total === 1 ? "" : "es"} — postcodes outside the instant-quote area where a visitor tried to get a price.
                Daisy auto-invites these visitors to leave contact details.
              </div>
            </div>
            <button data-testid="missed-postcodes-toggle" onClick={() => setExpanded(!expanded)} className="de-btn-secondary text-[11px] py-1 px-3">
              {expanded ? "Collapse" : "Show details"}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2" data-testid="missed-postcodes-groups">
            {topGroups.map(g => (
              <span key={g.outward} data-testid={`missed-postcode-outward-${g.outward}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-white border border-[#E5E7EB]">
                <b className="font-mono">{g.outward}</b>
                <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">×{g.count}</span>
                {g.converted > 0 && <span title="Converted into a Daisy enquiry" className="text-[10px] uppercase tracking-wider text-[#1F6B3B]">→ {g.converted} lead{g.converted === 1 ? "" : "s"}</span>}
              </span>
            ))}
            {groups.length > 6 && !expanded && (
              <span className="text-[11px] text-[#6B7280] self-center">+{groups.length - 6} more</span>
            )}
          </div>

          {expanded && (
            <div className="mt-4 border-t border-[#F0D5B4] pt-3">
              <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">Recent failed searches</div>
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-xs" data-testid="missed-postcodes-table">
                  <thead className="bg-white text-left">
                    <tr>{["Postcode", "Outward", "When", "Source", "Lead"].map(h => <th key={h} className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#F0D5B4]">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {recent.map(r => (
                      <tr key={r.id} data-testid={`missed-postcode-row-${r.id}`} className="border-b border-[#F0D5B4]/60">
                        <td className="px-2 py-1.5 font-mono font-bold text-[#12263A]">{r.postcode}</td>
                        <td className="px-2 py-1.5 font-mono text-[#6B7280]">{r.outward}</td>
                        <td className="px-2 py-1.5 text-[#6B7280]">{new Date(r.created_at).toLocaleString("en-GB")}</td>
                        <td className="px-2 py-1.5 text-[#6B7280]">{r.source || "quote"}</td>
                        <td className="px-2 py-1.5">{r.converted_enquiry_id
                          ? <span className="text-[#1F6B3B] font-bold">✓ enquiry</span>
                          : <span className="text-[#6B7280]">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
